import React, { useState, useEffect } from 'react';
import Icon from '../../../components/AppIcon';


const ThematicGrouping = ({ nodes, links, playTitle }) => {
  const [selectedTheme, setSelectedTheme] = useState(null);
  const [thematicClusters, setThematicClusters] = useState([]);

  // Define themes based on common Shakespeare play themes
  const themes = [
    { id: 'love', name: 'Love & Romance', icon: 'Heart', color: 'bg-red-500', keywords: ['love', 'romance', 'marriage', 'passion'] },
    { id: 'power', name: 'Power & Politics', icon: 'Crown', color: 'bg-yellow-500', keywords: ['power', 'king', 'queen', 'throne', 'rule'] },
    { id: 'betrayal', name: 'Betrayal & Conflict', icon: 'Swords', color: 'bg-orange-500', keywords: ['betrayal', 'conflict', 'enemy', 'revenge'] },
    { id: 'honor', name: 'Honor & Duty', icon: 'Shield', color: 'bg-blue-500', keywords: ['honor', 'duty', 'loyalty', 'noble'] },
    { id: 'family', name: 'Family Bonds', icon: 'Users', color: 'bg-green-500', keywords: ['family', 'father', 'mother', 'son', 'daughter'] }
  ];

  useEffect(() => {
    if (nodes?.length > 0) {
      analyzeThematicClusters();
    }
  }, [nodes, links]);

  const analyzeThematicClusters = () => {
    // Simple clustering based on connection strength
    // In a real implementation, this would use more sophisticated algorithms
    const clusters = themes?.map(theme => {
      // Get characters with high centrality (many connections)
      const characterConnections = nodes?.map(node => {
        const connections = links?.filter(
          l => l?.source === node?.id || l?.target === node?.id ||
               l?.source?.id === node?.id || l?.target?.id === node?.id
        )?.length;
        return { ...node, connections };
      });

      // Sort by connections and take top characters
      const topCharacters = characterConnections
        ?.sort((a, b) => b?.connections - a?.connections)
        ?.slice(0, Math.min(5, Math.ceil(nodes?.length / 3)));

      return {
        theme,
        characters: topCharacters,
        size: topCharacters?.length
      };
    });

    setThematicClusters(clusters);
  };

  const handleThemeClick = (theme) => {
    setSelectedTheme(selectedTheme?.id === theme?.id ? null : theme);
  };

  if (!nodes || nodes?.length === 0) return null;

  return (
    <div className="bg-card border border-border rounded-lg p-6">
      <div className="flex items-center gap-2 mb-4">
        <Icon name="Layers" size={20} className="text-primary" />
        <h3 className="font-semibold text-foreground">Thematic Character Groupings</h3>
      </div>

      <p className="text-sm text-muted-foreground mb-4">
        Explore character clusters organized around major themes in {playTitle}. These groupings show which characters are most central to each thematic element.
      </p>

      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-3 mb-4">
        {themes?.map(theme => (
          <button
            key={theme?.id}
            onClick={() => handleThemeClick(theme)}
            className={`flex flex-col items-center gap-2 p-4 rounded-lg border-2 transition-all ${
              selectedTheme?.id === theme?.id
                ? 'border-primary bg-primary/5' :'border-border hover:border-primary/50 bg-background'
            }`}
          >
            <div className={`w-10 h-10 rounded-full ${theme?.color} flex items-center justify-center`}>
              <Icon name={theme?.icon} size={20} className="text-white" />
            </div>
            <span className="text-xs font-medium text-foreground text-center">{theme?.name}</span>
          </button>
        ))}
      </div>

      {selectedTheme && (
        <div className="p-4 bg-muted rounded-lg">
          <div className="flex items-center gap-2 mb-3">
            <div className={`w-8 h-8 rounded-full ${selectedTheme?.color} flex items-center justify-center`}>
              <Icon name={selectedTheme?.icon} size={16} className="text-white" />
            </div>
            <h4 className="font-semibold text-foreground">{selectedTheme?.name}</h4>
          </div>

          <div className="space-y-2">
            {thematicClusters
              ?.find(c => c?.theme?.id === selectedTheme?.id)
              ?.characters?.map((char, index) => (
                <div key={index} className="flex items-center justify-between p-2 bg-background rounded">
                  <div className="flex items-center gap-2">
                    <span className="text-sm font-medium text-foreground">{char?.name}</span>
                  </div>
                  <div className="flex items-center gap-3">
                    <span className="text-xs text-muted-foreground">{char?.lineCount} lines</span>
                    <span className="text-xs text-muted-foreground">{char?.connections} connections</span>
                  </div>
                </div>
              ))}
          </div>

          <div className="mt-3 p-3 bg-background rounded border border-border">
            <p className="text-xs text-muted-foreground">
              <Icon name="Info" size={14} className="inline mr-1" />
              These characters are identified as central to the <strong>{selectedTheme?.name?.toLowerCase()}</strong> theme based on their network position and connection patterns.
            </p>
          </div>
        </div>
      )}
    </div>
  );
};

export default ThematicGrouping;